import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, IconButton, Button,
  CircularProgress, Alert, TextField, Tooltip, Collapse, Avatar,
  FormControl, InputLabel, Select, MenuItem, Skeleton, Badge,
  InputAdornment, ToggleButton, ToggleButtonGroup, Fade
} from '@mui/material';
import {
  Restaurant, Agriculture, Warning, CloudQueue, Visibility, Gavel,
  Settings, ExpandMore, ExpandLess, FilterList, Refresh, Schedule,
  AttachFile, Person, Computer, Science, Cloud, KeyboardArrowDown,
  CalendarToday, Clear, Search, Circle, Download
} from '@mui/icons-material';
import { timelineAPI } from 'services/api';

// ============================================================================
// EVENT TYPE CONFIGURATION
// ============================================================================

const EVENT_TYPES = {
  feed: {
    icon: <Restaurant />,
    color: '#FF9800',
    bgColor: '#FFF3E0',
    label: 'Feed',
    description: 'Feeding events'
  },
  stocking: {
    icon: <Agriculture />,
    color: '#4CAF50',
    bgColor: '#E8F5E9',
    label: 'Stocking',
    description: 'Stocking events'
  },
  alert: {
    icon: <Warning />,
    color: '#F44336',
    bgColor: '#FFEBEE',
    label: 'Alert',
    description: 'System alerts'
  },
  weather: {
    icon: <CloudQueue />,
    color: '#2196F3',
    bgColor: '#E3F2FD',
    label: 'Weather',
    description: 'Weather events'
  },
  survey: {
    icon: <Visibility />,
    color: '#9C27B0',
    bgColor: '#F3E5F5',
    label: 'Survey',
    description: 'Survey reports'
  },
  claim: {
    icon: <Gavel />,
    color: '#E91E63',
    bgColor: '#FCE4EC',
    label: 'Claim',
    description: 'Claim events'
  },
  system: {
    icon: <Settings />,
    color: '#607D8B',
    bgColor: '#ECEFF1',
    label: 'System',
    description: 'System events'
  },
  note: {
    icon: <Person />,
    color: '#795548',
    bgColor: '#EFEBE9',
    label: 'Note',
    description: 'Manual notes'
  },
  policy: {
    icon: <Gavel />,
    color: '#3F51B5',
    bgColor: '#E8EAF6',
    label: 'Policy',
    description: 'Policy events'
  },
  harvest: {
    icon: <Agriculture />,
    color: '#8BC34A',
    bgColor: '#F1F8E9',
    label: 'Harvest',
    description: 'Harvest events'
  },
  mortality: {
    icon: <Warning />,
    color: '#D32F2F',
    bgColor: '#FFCDD2',
    label: 'Mortality',
    description: 'Mortality events'
  },
  water_quality: {
    icon: <Science />,
    color: '#00BCD4',
    bgColor: '#E0F7FA',
    label: 'Water Quality',
    description: 'Water quality readings'
  },
  medicine: {
    icon: <Science />,
    color: '#FF5722',
    bgColor: '#FBE9E7',
    label: 'Medicine',
    description: 'Medicine application'
  },
  maintenance: {
    icon: <Settings />,
    color: '#9E9E9E',
    bgColor: '#F5F5F5',
    label: 'Maintenance',
    description: 'Maintenance events'
  },
  inspection: {
    icon: <Visibility />,
    color: '#673AB7',
    bgColor: '#EDE7F6',
    label: 'Inspection',
    description: 'Inspection events'
  },
  document: {
    icon: <AttachFile />,
    color: '#3F51B5',
    bgColor: '#E8EAF6',
    label: 'Document',
    description: 'Document uploads'
  },
  external: {
    icon: <Cloud />,
    color: '#546E7A',
    bgColor: '#ECEFF1',
    label: 'External',
    description: 'External data'
  },
  lab_report: {
    icon: <Science />,
    color: '#9C27B0',
    bgColor: '#F3E5F5',
    label: 'Lab Report',
    description: 'Laboratory test reports'
  }
};

// ============================================================================
// SEVERITY CONFIGURATION
// ============================================================================

const SEVERITY_CONFIG = {
  critical: { color: '#D32F2F', label: 'Critical', bgColor: '#FFCDD2' },
  high: { color: '#F44336', label: 'High', bgColor: '#FFEBEE' },
  medium: { color: '#FF9800', label: 'Medium', bgColor: '#FFF3E0' },
  low: { color: '#4CAF50', label: 'Low', bgColor: '#E8F5E9' },
  info: { color: '#2196F3', label: 'Info', bgColor: '#E3F2FD' }
};

// ============================================================================
// SOURCE CONFIGURATION
// ============================================================================

const SOURCE_CONFIG = {
  farmer: { icon: <Person sx={{ fontSize: 14 }} />, label: 'Farmer', color: '#4CAF50' },
  system: { icon: <Computer sx={{ fontSize: 14 }} />, label: 'System', color: '#607D8B' },
  lab: { icon: <Science sx={{ fontSize: 14 }} />, label: 'Lab', color: '#9C27B0' },
  weather: { icon: <Cloud sx={{ fontSize: 14 }} />, label: 'Weather', color: '#2196F3' },
  admin: { icon: <Person sx={{ fontSize: 14 }} />, label: 'Admin', color: '#FF5722' },
  agent: { icon: <Person sx={{ fontSize: 14 }} />, label: 'Agent', color: '#795548' },
  aic_officer: { icon: <Person sx={{ fontSize: 14 }} />, label: 'AIC Officer', color: '#3F51B5' }
};

// ============================================================================
// TIMELINE EVENT ITEM COMPONENT
// ============================================================================

const TimelineEventItem = ({ event, isLast }) => {
  const [expanded, setExpanded] = useState(false);
  
  const eventConfig = EVENT_TYPES[event.event_type] || EVENT_TYPES.system;
  const severityConfig = SEVERITY_CONFIG[event.severity] || SEVERITY_CONFIG.info;
  const sourceConfig = SOURCE_CONFIG[event.source?.toLowerCase()] || SOURCE_CONFIG.system;

  const formatTime = (dateStr) => {
    if (!dateStr) return '--:--';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-IN', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }
    return date.toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'short',
      year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
    });
  };

  const hasDetails = event.description || event.event_data || event.attachments?.length > 0;

  return (
    <Box sx={{ display: 'flex', position: 'relative' }}>
      {/* Timeline connector line */}
      {!isLast && (
        <Box sx={{
          position: 'absolute',
          left: 19,
          top: 48,
          bottom: -16,
          width: 2,
          bgcolor: 'grey.200',
          zIndex: 0
        }} />
      )}

      {/* Timeline dot */}
      <Box sx={{ 
        position: 'relative',
        zIndex: 1,
        mr: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}>
        <Avatar sx={{ 
          width: 40, 
          height: 40, 
          bgcolor: eventConfig.bgColor,
          color: eventConfig.color,
          border: `2px solid ${eventConfig.color}`,
          boxShadow: event.is_significant ? `0 0 0 3px ${eventConfig.color}30` : 'none'
        }}>
          {React.cloneElement(eventConfig.icon, { sx: { fontSize: 20 } })}
        </Avatar>
      </Box>

      {/* Event content */}
      <Box sx={{ flex: 1, pb: 3, minWidth: 0 }}>
        <Card 
          sx={{ 
            borderRadius: 2,
            border: '1px solid',
            borderColor: expanded ? eventConfig.color : 'grey.200',
            borderLeft: `4px solid ${eventConfig.color}`,
            transition: 'all 0.2s ease',
            cursor: hasDetails ? 'pointer' : 'default',
            '&:hover': {
              borderColor: eventConfig.color,
              boxShadow: `0 2px 8px ${eventConfig.color}20`
            }
          }}
          onClick={() => hasDetails && setExpanded(!expanded)}
        >
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            {/* Header row */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ 
                    fontWeight: 600,
                    color: 'text.primary',
                    lineHeight: 1.3
                  }}>
                    {event.title || 'Untitled Event'}
                  </Typography>
                  {event.is_significant && (
                    <Chip 
                      label="Important" 
                      size="small" 
                      color="warning"
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                  )}
                  {event.is_claim_evidence && (
                    <Chip 
                      label="Evidence" 
                      size="small" 
                      color="error"
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                  )}
                </Box>
                
                {/* Meta info row */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  {/* Time */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Schedule sx={{ fontSize: 12, color: 'text.secondary' }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                      {formatDate(event.created_at)} • {formatTime(event.created_at)}
                    </Typography>
                  </Box>

                  {/* DOC day */}
                  {event.doc_day !== null && event.doc_day !== undefined && (
                    <Chip 
                      label={`DOC ${event.doc_day}`}
                      size="small"
                      sx={{ 
                        height: 18, 
                        fontSize: '0.65rem',
                        bgcolor: 'primary.50',
                        color: 'primary.main',
                        fontWeight: 600
                      }}
                    />
                  )}

                  {/* Event type */}
                  <Chip 
                    label={eventConfig.label}
                    size="small"
                    sx={{ 
                      height: 18, 
                      fontSize: '0.65rem',
                      bgcolor: eventConfig.bgColor,
                      color: eventConfig.color,
                      fontWeight: 500
                    }}
                  />

                  {/* Source */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 0.3,
                    color: sourceConfig.color
                  }}>
                    {sourceConfig.icon}
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 500 }}>
                      {sourceConfig.label}
                    </Typography>
                  </Box>

                  {/* Severity indicator */}
                  {event.severity && (
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 0.3
                    }}>
                      <Circle sx={{ fontSize: 8, color: severityConfig.color }} />
                      <Typography variant="caption" sx={{ 
                        fontSize: '0.65rem', 
                        color: severityConfig.color,
                        fontWeight: 500
                      }}>
                        {severityConfig.label}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Expand/collapse button */}
              {hasDetails && (
                <IconButton 
                  size="small" 
                  sx={{ 
                    ml: 1,
                    transition: 'transform 0.2s',
                    transform: expanded ? 'rotate(180deg)' : 'none'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpanded(!expanded);
                  }}
                >
                  <ExpandMore sx={{ fontSize: 20 }} />
                </IconButton>
              )}
            </Box>

            {/* Expandable details section */}
            <Collapse in={expanded}>
              <Box sx={{ 
                mt: 1.5, 
                pt: 1.5, 
                borderTop: '1px solid',
                borderColor: 'grey.100'
              }}>
                {/* Description */}
                {event.description && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ 
                      color: 'text.secondary', 
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      fontSize: '0.65rem'
                    }}>
                      Description
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: 'text.primary',
                      mt: 0.5,
                      lineHeight: 1.5
                    }}>
                      {event.description}
                    </Typography>
                  </Box>
                )}

                {/* Event data */}
                {event.event_data && Object.keys(event.event_data).length > 0 && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ 
                      color: 'text.secondary', 
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      fontSize: '0.65rem'
                    }}>
                      Details
                    </Typography>
                    <Box sx={{ 
                      mt: 0.5, 
                      p: 1, 
                      bgcolor: 'grey.50', 
                      borderRadius: 1,
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1
                    }}>
                      {Object.entries(event.event_data).map(([key, value]) => (
                        <Chip
                          key={key}
                          label={`${key.replace(/_/g, ' ')}: ${value}`}
                          size="small"
                          variant="outlined"
                          sx={{ 
                            height: 24, 
                            fontSize: '0.7rem',
                            textTransform: 'capitalize'
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Attachments */}
                {event.attachments && event.attachments.length > 0 && (
                  <Box>
                    <Typography variant="caption" sx={{ 
                      color: 'text.secondary', 
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      fontSize: '0.65rem'
                    }}>
                      Attachments ({event.attachments.length})
                    </Typography>
                    <Box sx={{ 
                      mt: 0.5,
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 0.5
                    }}>
                      {event.attachments.map((attachment, idx) => (
                        <Chip
                          key={idx}
                          icon={<AttachFile sx={{ fontSize: 14 }} />}
                          label={attachment.name || `File ${idx + 1}`}
                          size="small"
                          clickable
                          component="a"
                          href={attachment.url}
                          target="_blank"
                          sx={{ height: 24, fontSize: '0.7rem' }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Reference info */}
                {event.reference_id && (
                  <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'grey.200' }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                      Reference: {event.reference_type} #{event.reference_id}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Collapse>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

// ============================================================================
// FILTER PANEL COMPONENT
// ============================================================================

const FilterPanel = ({ filters, onFilterChange, onClear }) => {
  const eventTypeOptions = [
    { value: '', label: 'All Types' },
    ...Object.entries(EVENT_TYPES).map(([key, config]) => ({
      value: key,
      label: config.label
    }))
  ];

  const severityOptions = [
    { value: '', label: 'All Severities' },
    ...Object.entries(SEVERITY_CONFIG).map(([key, config]) => ({
      value: key,
      label: config.label
    }))
  ];

  const hasFilters = filters.eventType || filters.severity || filters.startDate || filters.endDate;

  return (
    <Box sx={{ 
      p: 2, 
      bgcolor: 'grey.50', 
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'grey.200',
      mb: 2
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterList sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Filters
          </Typography>
        </Box>
        {hasFilters && (
          <Button 
            size="small" 
            startIcon={<Clear sx={{ fontSize: 14 }} />}
            onClick={onClear}
            sx={{ fontSize: '0.75rem' }}
          >
            Clear All
          </Button>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {/* Event Type Filter */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel sx={{ fontSize: '0.8rem' }}>Event Type</InputLabel>
          <Select
            value={filters.eventType}
            label="Event Type"
            onChange={(e) => onFilterChange('eventType', e.target.value)}
            sx={{ fontSize: '0.8rem' }}
          >
            {eventTypeOptions.map(opt => (
              <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.8rem' }}>
                {opt.value && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1 
                  }}>
                    <Box sx={{ color: EVENT_TYPES[opt.value]?.color }}>
                      {React.cloneElement(EVENT_TYPES[opt.value]?.icon || <Settings />, { sx: { fontSize: 16 } })}
                    </Box>
                    {opt.label}
                  </Box>
                )}
                {!opt.value && opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Severity Filter */}
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel sx={{ fontSize: '0.8rem' }}>Severity</InputLabel>
          <Select
            value={filters.severity}
            label="Severity"
            onChange={(e) => onFilterChange('severity', e.target.value)}
            sx={{ fontSize: '0.8rem' }}
          >
            {severityOptions.map(opt => (
              <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.8rem' }}>
                {opt.value && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Circle sx={{ fontSize: 10, color: SEVERITY_CONFIG[opt.value]?.color }} />
                    {opt.label}
                  </Box>
                )}
                {!opt.value && opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Date Range Filters */}
        <TextField
          size="small"
          type="date"
          label="From"
          value={filters.startDate}
          onChange={(e) => onFilterChange('startDate', e.target.value)}
          InputLabelProps={{ shrink: true, sx: { fontSize: '0.8rem' } }}
          sx={{ width: 140 }}
          inputProps={{ style: { fontSize: '0.8rem' } }}
        />

        <TextField
          size="small"
          type="date"
          label="To"
          value={filters.endDate}
          onChange={(e) => onFilterChange('endDate', e.target.value)}
          InputLabelProps={{ shrink: true, sx: { fontSize: '0.8rem' } }}
          sx={{ width: 140 }}
          inputProps={{ style: { fontSize: '0.8rem' } }}
        />
      </Box>
    </Box>
  );
};

// ============================================================================
// LOADING SKELETON
// ============================================================================

const TimelineSkeleton = () => (
  <Box>
    {[1, 2, 3].map((i) => (
      <Box key={i} sx={{ display: 'flex', mb: 3 }}>
        <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="rounded" height={80} />
        </Box>
      </Box>
    ))}
  </Box>
);

// ============================================================================
// MAIN POND TIMELINE COMPONENT
// ============================================================================

const PondTimeline = ({ pondId, compact = false, maxHeight = 600 }) => {
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    eventType: '',
    severity: '',
    startDate: '',
    endDate: ''
  });
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const containerRef = useRef(null);
  
  const LIMIT = compact ? 10 : 25;

  // Load timeline events
  const loadTimeline = useCallback(async (isLoadMore = false) => {
    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setError(null);
      }
      
      const params = { 
        limit: LIMIT,
        offset: isLoadMore ? offset : 0
      };
      
      if (filters.eventType) params.eventType = filters.eventType;
      if (filters.significantOnly) params.significantOnly = true;
      
      const [eventsRes, statsRes] = await Promise.all([
        timelineAPI.getPondTimeline(pondId, params),
        !isLoadMore ? timelineAPI.getPondTimelineStats(pondId) : Promise.resolve({ data: stats })
      ]);
      
      const newEvents = eventsRes.data || [];
      
      if (isLoadMore) {
        setEvents(prev => [...prev, ...newEvents]);
      } else {
        setEvents(newEvents);
      }
      
      setStats(statsRes.data);
      setHasMore(newEvents.length === LIMIT);
      setOffset(isLoadMore ? offset + LIMIT : LIMIT);
      
    } catch (err) {
      console.error('Timeline error:', err);
      setError(err.response?.data?.detail || 'Failed to load timeline');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [pondId, filters, offset, stats, LIMIT]);

  // Initial load
  useEffect(() => {
    if (pondId) {
      setOffset(0);
      setEvents([]);
      loadTimeline(false);
    }
  }, [pondId, filters.eventType]);

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setOffset(0);
    setEvents([]);
  };

  // Clear all filters
  const handleClearFilters = () => {
    setFilters({
      eventType: '',
      severity: '',
      startDate: '',
      endDate: ''
    });
    setOffset(0);
    setEvents([]);
  };

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!containerRef.current || loadingMore || !hasMore) return;
    
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    
    if (scrollHeight - scrollTop - clientHeight < 100) {
      loadTimeline(true);
    }
  }, [loadTimeline, loadingMore, hasMore]);

  // Filter events by date range (client-side filtering)
  const filteredEvents = events.filter(event => {
    // Severity filter
    if (filters.severity && event.severity !== filters.severity) {
      return false;
    }
    
    // Date range filter
    if (filters.startDate) {
      const eventDate = new Date(event.created_at).toISOString().split('T')[0];
      if (eventDate < filters.startDate) return false;
    }
    if (filters.endDate) {
      const eventDate = new Date(event.created_at).toISOString().split('T')[0];
      if (eventDate > filters.endDate) return false;
    }
    
    return true;
  });

  // Group events by date
  const groupedEvents = filteredEvents.reduce((groups, event) => {
    const date = new Date(event.created_at).toDateString();
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(event);
    return groups;
  }, {});

  const formatGroupDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }
    return date.toLocaleDateString('en-IN', { 
      weekday: 'long',
      day: 'numeric', 
      month: 'long',
      year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
    });
  };

  // Loading state
  if (loading && events.length === 0) {
    return (
      <Card sx={{ borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Schedule sx={{ color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Pond Timeline
            </Typography>
          </Box>
          <TimelineSkeleton />
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card sx={{ borderRadius: 2 }}>
        <CardContent>
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          <Button 
            variant="outlined" 
            startIcon={<Refresh />}
            onClick={() => loadTimeline(false)}
          >
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ 
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'divider',
      height: compact ? 'auto' : '100%'
    }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mb: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Schedule sx={{ color: 'primary.main', fontSize: 22 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
              Pond Timeline
            </Typography>
            {stats && (
              <Chip 
                label={`${stats.total_events || 0} events`}
                size="small"
                variant="outlined"
                sx={{ height: 22, fontSize: '0.7rem' }}
              />
            )}
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title="Filter Events">
              <IconButton 
                size="small" 
                onClick={() => setShowFilters(!showFilters)}
                color={showFilters ? 'primary' : 'default'}
              >
                <Badge 
                  badgeContent={
                    (filters.eventType ? 1 : 0) + 
                    (filters.severity ? 1 : 0) + 
                    (filters.startDate ? 1 : 0) + 
                    (filters.endDate ? 1 : 0)
                  } 
                  color="primary"
                  sx={{ '& .MuiBadge-badge': { fontSize: 10, minWidth: 16, height: 16 } }}
                >
                  <FilterList sx={{ fontSize: 20 }} />
                </Badge>
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton 
                size="small" 
                onClick={() => {
                  setOffset(0);
                  setEvents([]);
                  loadTimeline(false);
                }}
                disabled={loading}
              >
                <Refresh sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Stats summary */}
        {stats && !compact && (
          <Box sx={{ 
            display: 'flex', 
            gap: 1.5, 
            mb: 2,
            flexWrap: 'wrap'
          }}>
            {stats.significant_events > 0 && (
              <Chip
                icon={<Warning sx={{ fontSize: 14 }} />}
                label={`${stats.significant_events} Important`}
                size="small"
                color="warning"
                sx={{ height: 24, fontSize: '0.7rem' }}
              />
            )}
            {stats.claim_evidence_count > 0 && (
              <Chip
                icon={<Gavel sx={{ fontSize: 14 }} />}
                label={`${stats.claim_evidence_count} Evidence`}
                size="small"
                color="error"
                sx={{ height: 24, fontSize: '0.7rem' }}
              />
            )}
            {Object.entries(stats.events_by_type || {}).slice(0, 4).map(([type, count]) => {
              const config = EVENT_TYPES[type] || EVENT_TYPES.system;
              return (
                <Chip
                  key={type}
                  icon={React.cloneElement(config.icon, { sx: { fontSize: 14, color: config.color } })}
                  label={`${count} ${config.label}`}
                  size="small"
                  sx={{ 
                    height: 24, 
                    fontSize: '0.7rem',
                    bgcolor: config.bgColor,
                    color: config.color,
                    '& .MuiChip-icon': { color: config.color }
                  }}
                />
              );
            })}
          </Box>
        )}

        {/* Filter panel */}
        <Collapse in={showFilters}>
          <FilterPanel 
            filters={filters}
            onFilterChange={handleFilterChange}
            onClear={handleClearFilters}
          />
        </Collapse>

        {/* Timeline content */}
        <Box 
          ref={containerRef}
          onScroll={handleScroll}
          sx={{ 
            maxHeight: compact ? 400 : maxHeight,
            overflow: 'auto',
            pr: 1,
            '&::-webkit-scrollbar': {
              width: 6
            },
            '&::-webkit-scrollbar-track': {
              bgcolor: 'grey.100',
              borderRadius: 3
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: 'grey.300',
              borderRadius: 3,
              '&:hover': {
                bgcolor: 'grey.400'
              }
            }
          }}
        >
          {filteredEvents.length === 0 ? (
            <Box sx={{ 
              textAlign: 'center', 
              py: 6,
              color: 'text.secondary'
            }}>
              <Schedule sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
              <Typography variant="body2" sx={{ mb: 1 }}>
                No timeline events yet
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                Events will appear here as activity occurs
              </Typography>
            </Box>
          ) : (
            <>
              {Object.entries(groupedEvents).map(([dateStr, dateEvents], groupIdx) => (
                <Box key={dateStr} sx={{ mb: 2 }}>
                  {/* Date header */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    mb: 1.5,
                    position: 'sticky',
                    top: 0,
                    bgcolor: 'background.paper',
                    zIndex: 1,
                    py: 0.5
                  }}>
                    <CalendarToday sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="caption" sx={{ 
                      fontWeight: 600, 
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      fontSize: '0.7rem'
                    }}>
                      {formatGroupDate(dateStr)}
                    </Typography>
                    <Box sx={{ flex: 1, height: 1, bgcolor: 'grey.200', ml: 1 }} />
                  </Box>

                  {/* Events for this date */}
                  {dateEvents.map((event, eventIdx) => (
                    <TimelineEventItem
                      key={event.id}
                      event={event}
                      isLast={groupIdx === Object.keys(groupedEvents).length - 1 && 
                              eventIdx === dateEvents.length - 1 && !hasMore}
                    />
                  ))}
                </Box>
              ))}

              {/* Load more indicator */}
              {loadingMore && (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  py: 2 
                }}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {/* End of timeline */}
              {!hasMore && filteredEvents.length > 0 && (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 2,
                  color: 'text.disabled'
                }}>
                  <Typography variant="caption">
                    — End of timeline —
                  </Typography>
                </Box>
              )}

              {/* Load more button (fallback) */}
              {hasMore && !loadingMore && (
                <Box sx={{ textAlign: 'center', pt: 1 }}>
                  <Button
                    size="small"
                    onClick={() => loadTimeline(true)}
                    endIcon={<KeyboardArrowDown />}
                    sx={{ fontSize: '0.75rem' }}
                  >
                    Load More Events
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default PondTimeline;
